import { callFirebaseFunction } from 'boot/firebase';
import { documentId } from 'firebase/firestore';
import htmlToPdfMake from 'html-to-pdfmake';
import {
  chain, chunk, cloneDeep, find, flatten, forEach, get, groupBy,
  includes, isArray, isDate,
  isEmpty, isEqual, isFunction, isObject, map, max, mean, range, reduce, sum, toNumber,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { CATEGORY_IDENTIFIERS } from 'src/constants/dynamicFields';
import { V2_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import { TASK_CHECKBOX_SIZE } from 'src/constants/pdf';
import { ADDENDUM_TRACKED_APPOINTMENT_FIELDS } from 'src/constants/sessionActivities';
import AddendumsDB from 'src/fb/addendums-db';
import AppointmentsDB from 'src/fb/appointments-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import Interactions from 'src/mixins/Interactions';
import PdfMakeExportMixin from 'src/mixins/PdfMakeExportMixin';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import Time from 'src/mixins/Time';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { clients as clientsStore } from 'src/store/clients';
import { programsTargets } from 'src/store/programsTargets';
import { promptLevels } from 'src/store/promptLevels';
import Arabic from 'src/utils/Arabic';
import { exportPdfFile, getPdfBase64 } from 'src/utils/ExportUtils';
import { logEvent } from 'src/utils/LogEvent';
import { fixAlignedListItems, fixEmptyParagraphs, fixTaskLists } from 'src/utils/PdfMakeUtils';

const IMAGE_KEY_WORD = 'sessionNotes';

const BULK_LAYOUT_DATA_CATEGORIES = [CATEGORY_IDENTIFIERS.SESSION, CATEGORY_IDENTIFIERS.APPOINTMENT];

/**
 * Data of entities related to a specific session activity. It is needed later to compound
 * {@link SessionNoteLayoutFieldsData} data.
 *
 * @typedef {{
 * sessionActivity: Object,
 * client?: (Object | null),
 * session?: (Object | null),
 * appointment?: (Object | null)
 * }} SessionNoteData
 */

/**
 * Compound session note layout fields data used for rendering notes header and footer dynamic fields.
 *
 * @typedef {{
 * client: (Object | null),
 * session: (Object | null),
 * appointment: (Object | null)
 * user: (Object | null)
 * }} SessionNoteLayoutFieldsData
 */

/** @typedef {{[sessionActivityId: string]: SessionNoteLayoutFieldsData[]}} BulkSessionNotesData */

/** @typedef {{
 * top: number,
 * bottom: number,
 * left: number,
 * right: number
 * }} PageMargings
 */

/**
 * @typedef {number} Page
 * @typedef {string} SessionActivityId
 * @typedef {Map<Page, SessionActivityId>} ActivitiesPagesMapping
 */

export default {
  data: () => {
    return {
      isSessionNotesContainsArabic: false,

      loadingLayoutDataState: new Set(),

      /** Helps reuse data on bulk notes export */
      layoutDataCache: {
        [CATEGORY_IDENTIFIERS.SESSION]: new Map(),
        [CATEGORY_IDENTIFIERS.APPOINTMENT]: new Map(),
      },
    };
  },

  mixins: [
    PdfMakeExportMixin,
    SessionNotesTemplates,
    Toolbox,
    Interactions,
    Time,
  ],

  computed: {
    ...mapState(promptLevels, ['promptLevelTemplates', 'activePromptLevels']),
    ...mapState(auth, ['connectedClinic', 'sessionNotesLayoutSettings', 'hasSessionNotesLayout', 'allowImmediateSync']),
    ...mapState(clientsStore, ['clients']),

    sessionNotesPdfMakeFont() {
      return (this.isUserLanguageArabic || this.isSessionNotesContainsArabic)
        ? 'Tajaval'
        : 'PTSerif';
    },

    isLoadingLayoutData() {
      return this.loadingLayoutDataState.size > 0;
    },

    isBackendPDFGeneration() {
      return V2_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration) && this.allowImmediateSync;
    },
  },

  watch: {
    isLoadingLayoutData(value) {
      if (value && !this.$q.loading.isActive) {
        return this.$q.loading.show();
      }

      if (!value && this.$q.loading.isActive) {
        this.$q.loading.hide();
      }
    },
  },

  methods: {
    ...mapActions(programsTargets, ['getProgramTargets']),
    ...mapActions(clientsStore, ['getStaffMemberByUser']),

    /**
     * Calculates the arithmetic mean score for rating scale trials,
     * safely rejecting empty trials and non-numeric fields.
     *
     * @param {Object[]} trials - Array of session trial data objects.
     * @returns {number|null} The calculated average score, or null if no valid numeric scores exist.
     */
    calculateRatingAverage(trials) {
      const scores = chain(trials)
        .reject(isEmpty)
        .map((t) => toNumber(t.scoreValue))
        .reject((n) => Number.isNaN(n))
        .value();

      return scores.length > 0 ? mean(scores) : null;
    }, // calculateRatingAverage()

    /**
     * @param {Object[]} contentPages
     * @param {SessionNoteLayoutFieldsData | BulkSessionNotesData} [layoutFieldsData]
     */
    async generateSessionNotesPdf(contentPages, layoutFieldsData = {}) {
      const pageMargins = {
        top: 30,
        bottom: 30,
        left: 30,
        right: 30,
      };

      const doc = {
        pageSize: this.pdfMakeDefaultPageSize,
        pageOrientation: 'portrait',
        content: [],
        images: {},
        defaultStyle: {
          font: this.sessionNotesPdfMakeFont,
          fontSize: 9,
          margin: [0, 0, 0, 15],
        },
        styles: {
          'table-element': {
            margin: [0, 15, 0, 15],
          },
          'signature-fields': {
            margin: this.signatureElementMargin,
          },
        },
      };

      const pageBreak = {
        text: '',
        style: 'normal',
        pageBreak: 'after',
      };

      const docObjects = chain(contentPages)
        .forEach((page, index) => {
          if (index !== contentPages.length - 1) {
            page.content.push(pageBreak);
          }
        })
        .flatten(contentPages)
        .value();

      doc.content = chain(docObjects)
        .reduce((acc, pdfPage) => {
          if (get(pdfPage, 'images', false) && !isEmpty(pdfPage.images)) {
            doc.images = { ...doc.images, ...pdfPage.images };
          }

          forEach(pdfPage.content, (originalItem) => {
            // Set additional div styles
            const item = cloneDeep(originalItem);
            if (get(item, 'nodeName', false) && item.nodeName === 'DIV') {
              item.style = ['div'];
            }

            acc.push(this.fixElementMargin(item));
          });

          return acc;
        }, [])
        .compact()
        .value();

      if (!isEmpty(this.sessionNotesLayoutSettings)) {
        const {
          pageHeaderContent,
          pageFooterContent,
          showPageFooter,
          showPageHeader,
        } = this.sessionNotesLayoutSettings;

        if (showPageHeader && pageHeaderContent) {
          const { renderFunction, layoutHeight } = this.provideSessionNoteLayout(
            pageHeaderContent,
            layoutFieldsData,
            doc,
          );
          doc.header = renderFunction;
          pageMargins.top += layoutHeight;
        }

        if (showPageFooter && pageFooterContent) {
          const { renderFunction, layoutHeight } = this.provideSessionNoteLayout(
            pageFooterContent,
            layoutFieldsData,
            doc,
          );
          doc.footer = renderFunction;
          pageMargins.bottom += layoutHeight;
        }
      }

      doc.pageMargins = [pageMargins.left, pageMargins.top, pageMargins.right, pageMargins.bottom];

      return doc;
    }, // generateSessionNotesPdf()

    /**
     * @param {string} pdfContent
     * @param {SessionNoteLayoutFieldsData | BulkSessionNotesData} layoutFieldsData
     * @param {Object} pdfDoc - pdfMake document object
     *
     * @return {{ layoutHeight: number, renderFunction: ((number, number) => ({stack, margin})) }}
     * layoutHeight - use to adjust margin of the page.
     *
     * renderFunction - to be set as `footer` or `header` property of the pdfMake document
     */
    provideSessionNoteLayout(pdfContent, layoutFieldsData, pdfDoc) {
      const isBulkExport = !layoutFieldsData.session && !!(pdfDoc);
      const prerenderFields = isBulkExport ? Object.values(layoutFieldsData)[0] : layoutFieldsData;

      // We should predict layout element height outside the render function as the function will be called
      // much later when it's already late.
      const prerenderedContent = this.renderTemplate(pdfContent, prerenderFields);
      const { content: prerenderedPdfElement } = this.getSessionNotePdfPage([], prerenderedContent);
      const layoutHeight = this.predictPdfElementHeight(prerenderedPdfElement);

      /** @type {ActivitiesPagesMapping} */
      const sessionActivitiesPagesMapping = new Map();

      // Either `doc.header` or `doc.footer`
      const renderFunction = (currentPage, pageCount) => {
        let layoutData = (layoutFieldsData || {});

        if (isBulkExport) {
          if (currentPage === 1) {
            /**
             * We should define which session activity we show on which page to render proper layout fields values.
             * Process this only on the first page processing as we won't need to redo this in the future.
             */
            this.mapSessionActivitiesWithPages(pdfDoc.content, sessionActivitiesPagesMapping);
          }

          layoutData = layoutFieldsData[sessionActivitiesPagesMapping.get(currentPage)]?.[0] || {};
        }

        const sessionNoteLayoutData = {
          sessionNote: { currentPage, pageCount },
          ...layoutData,
        };

        const layoutFields = this.fetchTemplateFields(pdfContent);
        const fieldTransformations = reduce(this.sessionNoteFieldPresets, (result, preset) => {
          if (isFunction(preset.format) && includes(layoutFields, preset.token)) {
            // eslint-disable-next-line no-param-reassign
            result[preset.token] = preset.format;
          }
          return result;
        }, {});

        const renderedContent = this.renderTemplate(
          pdfContent,
          sessionNoteLayoutData,
          fieldTransformations,
        );
        const { content } = this.getSessionNotePdfPage([], renderedContent);

        return {
          stack: content,
          margin: [30, 20, 30, 20],
        };
      };

      return {
        layoutHeight,
        renderFunction,
      };
    }, // provideSessionNoteLayout()

    /**
     * Loops across pdf document's content determining which session activity is displayed on which page
     *
     * @param {Object[]} content - content of the pdfMake document
     * @param {ActivitiesPagesMapping} activitiesPagesMapping - activity-page mapping storage
     */
    mapSessionActivitiesWithPages(content, activitiesPagesMapping) {
      let prevPageNumber = 0;

      content.forEach((contentItem, idx) => {
        // Even items - notes content element, odd items - page break element
        const isSessionActivityNotesPageContent = !(idx % 2);
        const currentPageNumber = prevPageNumber + 1;
        // eslint-disable-next-line no-underscore-dangle
        const sessionActivityId = contentItem._sessionActivityId;

        if (
          !isSessionActivityNotesPageContent
          || !sessionActivityId
          // Current page was already predefined on the previous iteration - continue mapping process.
          || activitiesPagesMapping.has(currentPageNumber)
        ) {
          return;
        }

        activitiesPagesMapping.set(currentPageNumber, sessionActivityId);

        if (contentItem.positions?.length === 0) {
          // We have no positions elements meaning it's a plain session notes. It will take only one page, no more.
          prevPageNumber = currentPageNumber;
          return;
        }

        /*
          Check if the page number of the latest session notes content element position is more that the current page.
          In that case we know that session notes takes more than one page to render, and we can predefine those
          pages in the mapping.
        */
        const lastPositionItem = contentItem.positions[contentItem.positions.length - 1];
        const { pageNumber: sessionActivityLastPage } = lastPositionItem;

        if (sessionActivityLastPage > currentPageNumber) {
          const additionalPagesCount = sessionActivityLastPage - currentPageNumber;
          for (let i = 1; i <= additionalPagesCount; i += 1) {
            activitiesPagesMapping.set((currentPageNumber + i), sessionActivityId);
          }
        }

        prevPageNumber = sessionActivityLastPage;
      });
    }, // mapSessionActivitiesWithPages()

    predictPdfElementHeight(pdfContent) {
      const traverseElements = (items) => items?.map((item) => {
        if (!item) {
          return 0;
        }
        if (!item.nodeName) {
          // html-to-pdfmake strips nodeName from <li> with block children (like checklist rows).
          if (Array.isArray(item.stack)) {
            return sum(traverseElements(item.stack));
          }
          return 0;
        }

        let margin = 0;
        if (Array.isArray(item.margin)) {
          if (item.margin.length === 4) {
            margin = (item.margin[1] || 0) + (item.margin[3] || 0);
          } else if (item.margin.length === 2) {
            margin = (item.margin[1] || 0) * 2;
          } else {
            margin = 0;
          }
        } else {
          margin = item.margin || 0;
        }

        // Checked before DIV: html-to-pdfmake keeps nodeName 'DIV' on a checklist row and moves its stack to columns.
        if (Array.isArray(item.columns) && item.columns.length) {
          return Math.max(0, ...item.columns.map((column) => {
            const [height = 0] = traverseElements([column]);
            // A text-only DIV column has no stack, so the DIV branch would measure only its margin.
            return !column.stack && column.text && column.nodeName !== 'P' ? height + 13 : height;
          })) + margin;
        }
        if (item.nodeName === 'P') {
          return 13 + margin;
        }
        if (['TH', 'TD', 'LI', 'DIV'].includes(item.nodeName)) {
          return sum(traverseElements(get(item, 'stack', []))) + margin;
        }
        if (item.nodeName === 'TABLE') {
          return chain(item)
            .get(['table', 'body'])
            .map((row) => (max(traverseElements(row))))
            .sum()
            .value() + margin;
        }
        if (item.nodeName === 'OL') {
          return sum(traverseElements(item.ol)) + margin;
        }
        if (item.nodeName === 'UL') {
          return sum(traverseElements(item.ul)) + margin;
        }
        if (item.nodeName === 'SVG') {
          return TASK_CHECKBOX_SIZE + margin;
        }
        return margin;
      });
      // Make sure the minimum height is 13, so we don't have empty layout elements if they
      // just contain text and no stack elements
      return Math.max(sum(traverseElements(pdfContent)), 13);
    }, // predictPdfElementHeight()

    /**
     * Gets all the necessary session activities' data to render layout fields
     *
     * @param {Object[]} sessionActivities
     * @return {Promise<BulkSessionNotesData>}
     */
    async getBulkLayoutFieldsData(sessionActivities) {
      const { pageFooterContent, pageHeaderContent } = this.sessionNotesLayoutSettings;

      const layoutFields = this.fetchTemplateFields(`${pageHeaderContent} ${pageFooterContent}`);
      const fieldsCategories = new Set();
      layoutFields.forEach((field) => fieldsCategories.add(field.split('.')[0]));

      const clients = fieldsCategories.has(CATEGORY_IDENTIFIERS.CLIENT)
        ? sessionActivities
          .map((activity) => this.clients.find((client) => client.id === activity.client))
          .filter((client) => client || null)
        : null;

      let sessions = null;
      let appointments = null;
      let staffMembers = null;

      try {
        if (fieldsCategories.has(CATEGORY_IDENTIFIERS.SESSION)) {
          sessions = await this.loadBulkLayoutData(sessionActivities, CATEGORY_IDENTIFIERS.SESSION);
        }

        if (fieldsCategories.has(CATEGORY_IDENTIFIERS.APPOINTMENT)) {
          appointments = await this.loadBulkLayoutData(sessionActivities, CATEGORY_IDENTIFIERS.APPOINTMENT);
        }

        if (fieldsCategories.has(CATEGORY_IDENTIFIERS.STAFF_MEMBER)) {
          staffMembers = await this.loadBulkStaffMemberData(sessionActivities);
        }
      } catch (err) {
        logEvent(err, {
          context: {
            context_data: {
              sessionActivities: sessionActivities.map(({ id }) => id),
            },
          },
        });
        throw new Error(err.message);
      } finally {
        this.loadingLayoutDataState.delete(CATEGORY_IDENTIFIERS.SESSION);
        this.loadingLayoutDataState.delete(CATEGORY_IDENTIFIERS.APPOINTMENT);
      }

      const allSessionNoteFieldData = map(sessionActivities, (sessionActivity) => {
        const data = {
          sessionActivity,
          client: clients?.find(({ id }) => sessionActivity.client === id) || null,
          session: sessions?.find(({ id }) => sessionActivity.session === id) || null,
          appointment: appointments?.find(({ id }) => sessionActivity.appointment === id) || null,
          staffMember: staffMembers?.find(({ user }) => sessionActivity.user === user) || null,
        };
        return this.compileSessionNoteInformation(
          data.session,
          data.client,
          data.sessionActivity,
          data.appointment,
          data.staffMember,
        );
      });

      return groupBy(allSessionNoteFieldData, 'session.sessionActivityId');
    }, // getBulkLayoutFieldsData()

    /**
     * Load session activities' sessions or appointments behind the global loader, for export flows
     * that block input while layout data loads. Background loads use fetchBulkLayoutData.
     *
     * @param {Object[]} sessionActivities
     * @param {CategoryIdentifier} category
     *
     * @return {Promise<(Object & {id: string})[]>}
     */
    async loadBulkLayoutData(sessionActivities, category) {
      if (!BULK_LAYOUT_DATA_CATEGORIES.includes(category)) {
        return null;
      }

      this.loadingLayoutDataState.add(category);
      try {
        return await this.fetchBulkLayoutData(sessionActivities, category);
      } finally {
        this.loadingLayoutDataState.delete(category);
      }
    }, // loadBulkLayoutData()

    /**
     * Load session activities' sessions or appointments through the layout data cache, without any
     * loading UI.
     *
     * @param {Object[]} sessionActivities
     * @param {CategoryIdentifier} category
     *
     * @return {Promise<(Object & {id: string})[]>}
     */
    async fetchBulkLayoutData(sessionActivities, category) {
      if (!BULK_LAYOUT_DATA_CATEGORIES.includes(category)) {
        return null;
      }

      let data = null;

      const dataIdsToFetch = [];
      const cachedData = sessionActivities
        .map((activity) => {
          const docId = activity[category];
          const cachedItem = this.layoutDataCache[category].get(docId);
          if (cachedItem) {
            return cachedItem;
          }

          if (docId) {
            dataIdsToFetch.push(docId);
          }

          return null;
        }).filter(Boolean);

      if (dataIdsToFetch.length === 0) {
        return cachedData;
      }

      const db = category === 'session' ? new SessionsDB() : new AppointmentsDB();
      // For whatever reason firebase sometimes cannot proceed more than 8 ids in one request (missing permission)
      if (dataIdsToFetch.length <= 8) {
        data = await db.readAll([[documentId(), 'in', dataIdsToFetch]]);
      } else {
        const chunkedIds = chunk(dataIdsToFetch, 8);
        data = await Promise.all(chunkedIds.map(async (idsChunk) => {
          try {
            return await db.readAll([[documentId(), 'in', idsChunk]]);
          } catch (err) {
            // We want to see what chunk failed the request
            // todo: consider adding this info to the Bugsnag event
            console.error(err.message, { documentsIds: idsChunk });
            throw err;
          }
        }));
        data = flatten(data);
      }

      data.forEach((item) => this.layoutDataCache[category].set(item.id, item));

      return [...data, ...cachedData];
    }, // fetchBulkLayoutData()

    async loadBulkStaffMemberData(sessionActivities) {
      this.loadingLayoutDataState.add(CATEGORY_IDENTIFIERS.STAFF_MEMBER);
      let staffMembers = [];
      try {
        const staffMemberActions = sessionActivities.map(({ user }) => (this.getStaffMemberByUser(user)));
        staffMembers = (await Promise.all(staffMemberActions)).filter(Boolean);
      } finally {
        this.loadingLayoutDataState.delete(CATEGORY_IDENTIFIERS.STAFF_MEMBER);
      }
      return staffMembers;
    }, // loadBulkStaffMemberData()

    /**
     * Gets all the necessary data by provided session activity to render layout fields
     *
     * @param {SessionNoteData} sessionNoteData
     * @return {Promise<SessionNoteLayoutFieldsData>}
     */
    async getLayoutFieldsData({ client = null, sessionActivity, session = null, appointment = null }) {
      if (isEmpty(sessionActivity) || !this.hasSessionNotesLayout) {
        return {};
      }

      const { pageFooterContent, pageHeaderContent } = this.sessionNotesLayoutSettings;

      // Optimization: check if we have specific fields to avoid fetching unnecessary data
      const layoutFields = this.fetchTemplateFields(`${pageHeaderContent} ${pageFooterContent}`);
      const fieldsCategories = new Set();
      layoutFields.forEach((field) => fieldsCategories.add(field.split('.')[0]));

      const clientData = fieldsCategories.has(CATEGORY_IDENTIFIERS.CLIENT)
        ? (client || this.clients.find(({ id }) => id === sessionActivity.client) || null)
        : null;

      const sessionData = fieldsCategories.has(CATEGORY_IDENTIFIERS.SESSION)
        ? (session || (await this.loadLayoutData(sessionActivity.session, CATEGORY_IDENTIFIERS.SESSION))
          || null)
        : null;
      const appointmentData = fieldsCategories.has(CATEGORY_IDENTIFIERS.APPOINTMENT)
        ? (appointment || (await this.loadLayoutData(sessionActivity.appointment, CATEGORY_IDENTIFIERS.APPOINTMENT))
          || null)
        : null;
      const staffMember = fieldsCategories.has(CATEGORY_IDENTIFIERS.STAFF_MEMBER)
        ? (await this.getStaffMemberByUser(sessionActivity.user)
          || null)
        : null;
      return this.compileSessionNoteInformation(sessionData, clientData, sessionActivity, appointmentData, staffMember);
    },

    /**
     * Loads session or activity documents along with caching the result in the component state
     *
     * @param {string} docId
     * @param {CategoryIdentifier} category
     *
     * @return {Promise<Object | null>}
     */
    async loadLayoutData(docId, category) {
      if (!docId || ![CATEGORY_IDENTIFIERS.SESSION, CATEGORY_IDENTIFIERS.APPOINTMENT].includes(category)) {
        return null;
      }

      const cachedDocument = this.layoutDataCache[category].get(docId);
      if (cachedDocument) {
        return cachedDocument;
      }

      const db = category === CATEGORY_IDENTIFIERS.SESSION ? new SessionsDB() : new AppointmentsDB();
      let docData = null;

      const loadingId = `${category}_${docId}`;
      this.loadingLayoutDataState.add(loadingId);
      try {
        docData = await db.read(docId);
        this.layoutDataCache[category].set(docId, docData);
      } catch (err) {
        console.error(err);
        logEvent(err, {
          context: {
            context_data: { documentId: docId },
          },
        });
        throw new Error(err.message);
      } finally {
        this.loadingLayoutDataState.delete(loadingId);
      }

      return docData;
    }, // loadSessionData()

    /**
     * Export and download session notes to PDF
     *
     * @public
     * @param {Object[]} contentPages
     * @param {SessionNoteLayoutFieldsData | BulkSessionNotesData} [layoutFieldsData]
     */
    async downloadSessionNotesPdf(contentPages, layoutFieldsData = {}) {
      const doc = await this.generateSessionNotesPdf(contentPages, layoutFieldsData);
      const fileName = 'HiRasmus-SessionNote.pdf';

      if (this.$q.platform.is.capacitor) {
        return this.downloadPdfOnNativeApp(doc, fileName);
      }

      return exportPdfFile(doc, fileName);
    }, // downloadSessionNotesPdf()

    /**
     * Export session notes to PDF blob
     *
     * @public
     * @param {Object[]} contentPages
     * @param {string} clinicId
     * @param {string} sessionActivityId
     * @param {SessionNoteLayoutFieldsData} [layoutFieldData]
     */
    async getSessionNotesPdf(contentPages, clinicId, sessionActivityId, layoutFieldData = {}) {
      const doc = await this.generateSessionNotesPdf(contentPages, layoutFieldData);
      if (this.isBackendPDFGeneration) {
        const generationRes = await callFirebaseFunction('generateAndSavePDF', {
          doc,
          clinicId,
          sessionActivityId,
          footer: doc.footer?.(),
          header: doc.header?.(),
          timezone: this.currentIANATimezone,
        });
        return generationRes.data;
      }
      return getPdfBase64(doc);
    }, // getSessionNotesPdf()

    /**
     * @param {string} sessionId
     * @param {string} clientId
     * @param {Object[] | []} allPrograms
     * @return {Promise<Awaited<[[string,string,string,string]]|[string,*,string,string][]>[]>}
     */
    async exportSessionDataToPdf(sessionId, clientId, allPrograms) {
      const sessionProgramsDb = new SessionProgramsDB(sessionId);
      const sessionPrograms = await sessionProgramsDb.readWithData('order', 'asc', clientId);
      return Promise.all(
        map(sessionPrograms, async (sessionProgram) => {
          const programPromptLevelTemplate = get(sessionProgram, ['program', 'promptLevelTemplate'], null);
          const programId = get(sessionProgram.program, 'id', sessionProgram.program);
          const sessionProgramData = chain(sessionProgram)
            .get(['data', 'data'])
            .map((dataItem) => ({
              ...dataItem,
              promptLevelDetails: chain(this.promptLevelTemplates)
                .find({
                  id: dataItem.promptLevelTemplate || sessionProgram.data.promptLevelTemplate,
                })
                .get('promptLevels')
                .find({ id: dataItem.promptLevel })
                .value(),
            }))
            .value();

          let programTargets = await this.getProgramTargets({
            program: programId,
            targets:
            chain(sessionProgramData)
              .map((data) => data.parentTarget)
              .compact()
              .uniqWith(isEqual)
              .value(),
          });

          const targetsWithData = await this.getProgramTargets({
            program: programId,
            targets:
            chain(sessionProgramData)
              .map((data) => data.target)
              .uniqWith(isEqual)
              .value(),
          });

          programTargets = chain(programTargets)
            .concat(targetsWithData)
            .uniqBy('id')
            .reject((target) => ((!!target.parentTarget) && !find(programTargets, { id: target.parentTarget })))
            .value();

          const maxTrial = chain(sessionProgramData).maxBy('trial').get('trial').value();
          const parentTargetOrders = chain(programTargets)
            .reject('parentTarget')
            .groupBy('id')
            .mapValues((target) => chain(target).head().get('order').value())
            .value();

          const mappedData = chain(programTargets)
            .compact()
            .map((programTarget) => ({
              id: programTarget.id,
              parentTarget: programTarget.parentTarget,
              dataType: programTarget.dataType || null,
              parentTargetDataType: chain(programTargets)
                .find({ id: programTarget.parentTarget })
                .get('dataType', null)
                .value(),
              targetLabel: programTarget.label,
              discriminativeStimulus: programTarget.discriminativeStimulus,
              promptLevelTemplate: programTarget.promptLevelTemplate || programPromptLevelTemplate,
              aggregatedData: {},
              hasData: !chain(sessionProgramData).find({ target: programTarget.id }).isUndefined().value(),
              trials: chain(range(1, maxTrial + 1))
                .map((trial) => chain(sessionProgramData)
                  .find({ target: programTarget.id, trial })
                  .defaults({})
                  .value())
                .value(),
              order: (programTarget.parentTarget)
                ? get(parentTargetOrders, programTarget.parentTarget) * 1000 + programTarget.order
                : programTarget.order * 1000,
            }))
            .orderBy('order')
            .value();

          const data = chain(mappedData)
            .map((programTarget) => {
              let aggregatedData;
              let hasData = get(programTarget, 'hasData', false);
              if (!hasData) {
                aggregatedData = {
                  trials: null,
                  successes: null,
                  duration: null,
                  successfulDuration: null,
                };
              }

              if (['taskAnalysis', 'targetSet'].includes(programTarget.dataType)) {
                aggregatedData = {
                  trials: chain(mappedData)
                    .filter({ parentTarget: programTarget.id })
                    .flatMap('trials')
                    .reject(isEmpty)
                    .reject({ scoreValue: null })
                    .size()
                    .value(),
                  successes: chain(mappedData)
                    .filter({ parentTarget: programTarget.id })
                    .flatMap('trials')
                    .filter({ scoreValue: 100 })
                    .size()
                    .value(),
                  duration: chain(mappedData)
                    .filter({ parentTarget: programTarget.id })
                    .flatMap('trials')
                    .sumBy('duration')
                    .defaultTo(0)
                    .value(),
                  successfulDuration: chain(mappedData)
                    .filter({ parentTarget: programTarget.id })
                    .flatMap('trials')
                    .filter({ scoreValue: 100 })
                    .sumBy('duration')
                    .defaultTo(0)
                    .value(),
                  isRating: chain(mappedData)
                    .filter({ parentTarget: programTarget.id })
                    .flatMap('trials')
                    .some('isRatingScale')
                    .value(),
                  average: this.calculateRatingAverage(
                    chain(mappedData)
                      .filter({ parentTarget: programTarget.id })
                      .flatMap('trials')
                      .value(),
                  ),
                };
                hasData = (aggregatedData.trials > 0);
              } else {
                aggregatedData = {
                  trials: chain(programTarget.trials)
                    .reject(isEmpty)
                    .reject({ scoreValue: null })
                    .size()
                    .value(),
                  successes: chain(programTarget.trials)
                    .filter({ scoreValue: 100 })
                    .size()
                    .value(),
                  duration: chain(programTarget.trials)
                    .sumBy('duration')
                    .defaultTo(0)
                    .value(),
                  successfulDuration: chain(programTarget.trials)
                    .filter({ scoreValue: 100 })
                    .sumBy('duration')
                    .defaultTo(0)
                    .value(),
                  isRating: chain(programTarget.trials)
                    .some('isRatingScale')
                    .value(),
                  average: this.calculateRatingAverage(programTarget.trials),
                };
              }
              return {
                ...programTarget,
                hasData,
                aggregatedData,
              };
            })
            .value();
          return this.prepareSessionProgramDataForExport(sessionProgram, data, allPrograms);
        }),
      );
    }, // exportSessionDataToPdf()

    /**
     * Returns data formatted for exporting for a specfic sessionProgram
     *
     * @param {Object} sessionProgram Session program object
     * @param {Array[]} data Session data
     * @param {Object[] | []} allPrograms
     */
    prepareSessionProgramDataForExport(sessionProgram, data, allPrograms) {
      const foundInStoreProgram = find(allPrograms, { id: sessionProgram.program });
      const program = isObject(sessionProgram.program) ? sessionProgram.program : foundInStoreProgram;
      const dataType = get(sessionProgram, ['data', 'dataType']);
      const programName = foundInStoreProgram?.programName || sessionProgram?.programName || '';

      if (dataType === 'Task1.0') {
        const targetName = '';
        const trials = '';
        let successRate = '';
        const numSuccesses = data.reduce((pV, target) => pV + target.aggregatedData.successes, 0);

        const numTrials = chain(data)
          .reduce((pV, target) => pV + target.aggregatedData.trials, 0)
          .value();

        if (numTrials > 0) {
          successRate = `${Math.round((numSuccesses / numTrials) * 100)}%`;
        }

        return [[programName, targetName, trials, successRate]];
      }

      return map(data, (target) => {
        const targetName = target.targetLabel;
        let trials = '';

        const promptLevelTemplate = get(sessionProgram, ['data', 'promptLevelTemplate'], '');

        const promptLevelCount = chain(this.activePromptLevels)
          .find({ id: promptLevelTemplate })
          .get('promptLevels')
          .size()
          .value();

        if (promptLevelCount > 1) {
          trials = chain(target)
            .get('trials')
            .map((trial) => get(trial, ['promptLevelDetails', 'trialOutcome']))
            .compact()
            .join(' ')
            .value();
        }
        let successRate = '';

        if (target.aggregatedData?.isRating) {
          const avgScore = target.aggregatedData.average;
          const resultDisplay = Number.isFinite(avgScore) ? `${avgScore.toFixed(1)}` : '-';
          return [programName, targetName, trials, resultDisplay];
        }

        const dataTypeInfo = target.dataType || get(program, 'type');
        switch (dataTypeInfo) {
          case 'frequency':
            if (get(sessionProgram, ['data', 'duration']) > 0) {
              successRate = `${target.aggregatedData.successes}`;
            }
            break;
          case 'duration':
            successRate = `${this.formatDuration(target.aggregatedData.successfulDuration)}`;
            break;
          default: {
            if (target.aggregatedData.trials > 0) {
              successRate = `${Math.round((target.aggregatedData.successes / target.aggregatedData.trials) * 100)}%`;
            }
          }
        }

        return [programName, targetName, trials, successRate];
      });
    }, // prepareSessionProgramDataForExport

    /** Generates HTML string with session content.
     *
     * @private
     * @param {Array} exportTable
     * @param {string} notes
     * @param {Object[]} signatures
     * @param {boolean?} isAddSignaturePlaceholder
     */
    generateHtmlForPdf(exportTable, notes, signatures, isAddSignaturePlaceholder = false) {
      let exportData = this.generateSessionNotesHtml(notes, signatures, isAddSignaturePlaceholder);

      // additional info for table in pdf
      if (!isEmpty(exportTable)) {
        // initial array for thead
        const headArr = ['PROGRAM', 'TARGET', 'PROMPT CODES', 'RESULT'];
        const tHead = `<table><thead><tr style="border-top:1px solid #000;border-bottom:1px solid #000;">${
          headArr.map((thElement) => `<th style="padding-right:32px;">${thElement}</th>`).join('')
        }</tr></thead>`;
        exportData += `${tHead}<tbody>`;

        const trBorderBottom = '<tr style="border-bottom:1px solid #000">';
        const trEl = chain(exportTable)
          .flatten()
          .reduce((prev, curr) => {
            const trContent = map(curr, (tdEl) => `<td style="padding-right:32px;">${tdEl}</td>`).join('');

            return `${prev}${trBorderBottom}${trContent}</tr>`;
          }, '')
          .value();

        exportData += trEl;
        exportData += '</tbody></table>';
      }

      exportData = this.fixMergedCells(exportData);
      exportData = this.alignTemplateImages(exportData);

      return exportData;
    }, // generateHtmlForPdf()

    /**
     * @param {Object} sessionActivity
     * @return {Promise<{senderInfo: {senderId: string, senderRefId: string | null}, base64pdf: string}>}
     */
    async getSessionActivityNotesDataToTransfer(sessionActivity) {
      const isAlohaIntegration = this.connectedClinic.integration === 'aloha';
      const layoutFieldsData = !isAlohaIntegration
        ? await this.getLayoutFieldsData({ sessionActivity })
        : null;
      const isAddSignaturePlaceholder = this.isBackendPDFGeneration && !sessionActivity.signatures?.length;

      const contentPages = [this.getSessionNotePdfPage(
        [],
        sessionActivity.notes,
        sessionActivity.signatures,
        0,
        isAddSignaturePlaceholder,
      )];
      const base64pdf = !isAlohaIntegration
        ? await this.getSessionNotesPdf(contentPages, sessionActivity.clinic, sessionActivity.id, layoutFieldsData)
        : '';
      const senderInfo = {
        senderId: this.userDetails.userId,
        senderRefId: get(this.userDetails, 'integrationReference', null),
      };

      return {
        base64pdf,
        senderInfo,
      };
    }, // getSessionActivityNotesDataToTransfer()

    /**
     * Creating element for pdf document.
     *
     * @param {Array} exportElement - object of element that should be converted into doc.content object
     * @param {string} notes
     * @param {Object[]} [signatures]
     * @param {number?} index
     * @param {boolean?} isAddSignaturePlaceholder
     * @returns {{content: Object[], images: Object}} {content, images}
     */
    getSessionNotePdfPage(exportElement, notes, signatures = [], index = 0, isAddSignaturePlaceholder = false) {
      const notesWithSpaces = fixEmptyParagraphs(notes);
      const htmlString = this.generateHtmlForPdf(
        exportElement,
        notesWithSpaces,
        signatures,
        isAddSignaturePlaceholder,
      );
      this.isSessionNotesContainsArabic = Arabic.isContainedInString(htmlString);
      const fixedTablesTemplate = fixAlignedListItems(fixTaskLists(this.fixTemplateTables(htmlString, true)));

      const pdfPage = htmlToPdfMake(
        fixedTablesTemplate
          .replace(
            /width:fit-content([;"])/,
            (match, lastSign) => (lastSign === '"' ? lastSign : ''),
          ),
        {
          imagesByReference: true,
          tableAutoSize: true,
          defaultStyles: this.pdfMakeDefaultStyles,
        },
      );
      if (!isArray(pdfPage.content)) {
        pdfPage.content = [pdfPage.content];
      }

      if (exportElement) {
        // Adding outer margins to all tables with the session data
        pdfPage.content.forEach((contentItem) => {
          if (contentItem?.nodeName === 'TABLE') {
            contentItem.style = [...contentItem.style, 'table-element'];
          }
        });
      }

      // Fixing image reference
      pdfPage.content = map(pdfPage.content, (pdfPageContent) => {
        return this.getFixedSessionNotesPdfPageContent(pdfPageContent, index);
      });

      pdfPage.images = reduce(pdfPage.images, (acc, value, key) => {
        acc[`${key}-${IMAGE_KEY_WORD}-${index}`] = value;
        return acc;
      }, {});

      return pdfPage;
    }, // getSessionNotePdfPage()

    /**
     * @param {Array} pdfData
     * @param {Object} sessionActivity
     * @param {number} idx
     * @return {{content: Object[], images: Object}}
     */
    getSessionNotePdfPageForBulkExport(pdfData, sessionActivity, idx) {
      const pageContent = this.getSessionNotePdfPage(
        pdfData,
        sessionActivity.notes,
        sessionActivity.signatures,
        idx,
      );

      /**
       * Should save session activity id within the pdf content to determine which session activity
       * displayed on which page rendering header/footer of session notes
       */
      pageContent.content.forEach((data) => {
        // eslint-disable-next-line no-underscore-dangle
        data._sessionActivityId = sessionActivity.id;
      });

      return pageContent;
    }, // getSessionNotePdfPageForBulkExport

    /**
     * @private
     * @param {string} value
     * @return {string}
     */
    escapeAddendumHtmlValue(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }, // escapeAddendumHtmlValue()

    /**
     * Generates the HTML body of an addendum pdf page: title line with the
     * creation date and source, then one row per changed field. The signatures
     * block is appended by the regular session notes pipeline.
     *
     * @private
     * @param {Object} addendum
     * @return {string}
     */
    generateAddendumHtml(addendum) {
      const { createTimestamp } = addendum;
      const createdDate = isDate(createTimestamp) ? createTimestamp : createTimestamp?.toDate();
      const title = this.$t('addendums.pdf.title', {
        date: createdDate ? createdDate.toLocaleDateString(undefined, { hour: 'numeric', minute: '2-digit' }) : '',
        source: this.$t(`addendums.sources.${addendum.source}`),
      });

      let html = `<h3>${title}</h3>`;

      const changedFields = addendum.changedFields || {};
      const fieldRows = ADDENDUM_TRACKED_APPOINTMENT_FIELDS
        .filter((field) => field in changedFields)
        .map((field) => {
          const label = this.$t(`auditLogReports.fields.${field}`);
          const value = this.escapeAddendumHtmlValue(changedFields[field] ?? '—');
          return `<tr><td style="padding-right:32px;"><b>${label}</b></td><td>${value}</td></tr>`;
        })
        .join('');

      html += `<table><tbody>${fieldRows}</tbody></table>`;

      return html;
    }, // generateAddendumHtml()

    /**
     * @param {Object} addendum
     * @param {number | string} index - unique image-key namespace for the page
     * @return {{content: Object[], images: Object}}
     */
    getAddendumPdfPage(addendum, index) {
      return this.getSessionNotePdfPage(
        [],
        this.generateAddendumHtml(addendum),
        addendum.signatures || [],
        index,
      );
    }, // getAddendumPdfPage()

    /**
     * Builds one pdf page per addendum of the activity, oldest first.
     *
     * @param {Object} sessionActivity
     * @param {number | string} [pageIndexPrefix] - prefix namespacing the addendum image keys
     * @return {Promise<Array<{content: Object[], images: Object}>>}
     */
    async getAddendumPdfPages(sessionActivity, pageIndexPrefix = 0) {
      const addendumsDb = new AddendumsDB(sessionActivity.id);
      const addendums = await addendumsDb.readChronological();

      return addendums.map((addendum, idx) => {
        const page = this.getAddendumPdfPage(addendum, `${pageIndexPrefix}-addendum-${idx}`);

        page.content.forEach((data) => {
          // eslint-disable-next-line no-underscore-dangle
          data._sessionActivityId = sessionActivity.id;
        });

        return page;
      });
    }, // getAddendumPdfPages()

    /**
     * Generate HTML string from session notes.
     *
     * @private
     * Used in {@link generateHtmlForPdf()}, {@link onCopySessionNotesToClipboard()}
     *
     * @param {string} notes
     * @param {Object[]} signatures
     * @returns {string} - Html string
     * @param {boolean?} isAddSignaturePlaceholder
    */
    generateSessionNotesHtml(notes, signatures, isAddSignaturePlaceholder = false) {
      let exportData = '<div>';

      let sessionNotes = notes;
      sessionNotes = sessionNotes.replace(/(?:\r\n|\r|\n)/g, '<br>');
      // we should clear all unnecessary styles from the notes, but <colgroup> and <col> elements should be untouched
      // because their styles are necessary
      sessionNotes = sessionNotes.replace(/<([a-zA-Z][^\s>]*)[^>]*\s+style="([^"]*)"/gm, (match, tagName, styles) => {
        const styleRegexp = /style="([^"]*)"/gm;
        // we should not clear style attribute of colgroup and col elements
        if (tagName === 'col' || tagName === 'colgroup') {
          return match;
        }

        if (includes(styles, 'text-align')) {
          const textAlignStyle = match.match(/(text-align: ?.*?)(;|")/gm);
          return match.replace(styleRegexp, `style="${textAlignStyle}"`);
        }

        return match.replace(styleRegexp, '');
      });
      sessionNotes = sessionNotes.replace(/class="([^"]*)"/gm, '');

      exportData += sessionNotes;

      if (!isEmpty(signatures)) {
        exportData += '<br>';

        forEach(signatures, ({ userName, timestamp, url, professionalCredentials }) => {
          let signDate = isDate(timestamp) ? timestamp : timestamp.toDate();

          signDate = this.getLocaleDateString(signDate, { options: { hour: 'numeric', minute: '2-digit' } });
          const image = `<img width="200px" src="${url}" id="signature-image">`;

          let credentialsText = professionalCredentials?.length
            ? professionalCredentials.join(', ')
            : '';

          if (credentialsText.length > 100) {
            credentialsText = `${credentialsText.slice(0, 100).trimEnd()}...`;
          }

          const nameWithCredentials = credentialsText.length
            ? `${userName}, ${credentialsText}`
            : userName;
          exportData += `<div>${image}<span><b>${nameWithCredentials}</b> ${signDate}</span></div>`;
        });
      } else if (isAddSignaturePlaceholder) {
        exportData += '<br>';
        const image = '<img width="200px" src="src-placeholder" id="signature-image">';
        exportData += `<div id="signature-placeholder">${image}<span><b>Name</b> Date</span></div>`;
      }
      exportData += '</div>';
      return exportData;
    }, // generateSessionNotesHtml()

    /**
     * Recursively finds all IMG elements in pdfPageContent item, fixing its reference
     * and returns new pdfPageContent item.
     *
     * @param {Object} pdfPageContent
     * @param {number} index
     * @returns {Object} fixed pdfPageContent object
     */
    getFixedSessionNotesPdfPageContent(pdfPageContent, index = 0) {
      let pdfPageContentCopy = cloneDeep(pdfPageContent);

      // if elem has text and text has Arabic language change direction
      if ((get(pdfPageContentCopy, 'text') && Arabic.isPrevailInString(this.getTextFromNode(pdfPageContentCopy)))) {
        pdfPageContentCopy.direction = 'rtl';
      }

      if (get(pdfPageContentCopy, 'nodeName') === 'SPAN') {
        pdfPageContentCopy.style = [...pdfPageContentCopy.style, 'signature-fields'];
      }

      if (get(pdfPageContentCopy, 'nodeName') === 'IMG') {
        pdfPageContentCopy.image += `-${IMAGE_KEY_WORD}-${index}`;
        pdfPageContentCopy.width *= this.pdfFactor;

        return pdfPageContentCopy;
      }

      if (get(pdfPageContentCopy, 'stack', false) && !isEmpty(pdfPageContentCopy.stack)) {
        pdfPageContentCopy.stack = map(
          pdfPageContentCopy.stack,
          (stackItem) => this.getFixedSessionNotesPdfPageContent(stackItem, index),
        );
      }

      // if elem is <ul>
      if (get(pdfPageContentCopy, 'ul') && isArray(pdfPageContentCopy.ul) && pdfPageContentCopy.ul.length) {
        pdfPageContentCopy.ul = map(pdfPageContentCopy.ul, (ul) => {
          return this.getFixedSessionNotesPdfPageContent(ul, index);
        });
      }

      // if elem is <ol>
      if (get(pdfPageContentCopy, 'ol') && isArray(pdfPageContentCopy.ol) && pdfPageContentCopy.ol.length) {
        pdfPageContentCopy.ol = map(pdfPageContentCopy.ol, (ol) => {
          return this.getFixedSessionNotesPdfPageContent(ol, index);
        });
      }

      if (Array.isArray(pdfPageContentCopy.columns) && pdfPageContentCopy.columns.length) {
        pdfPageContentCopy.columns = pdfPageContentCopy.columns.map(
          (column) => this.getFixedSessionNotesPdfPageContent(column, index),
        );
      }

      // fix all tables widths
      const isElemTable = get(pdfPageContentCopy, 'table.widths') && isArray(pdfPageContentCopy.table.widths);
      const isTableBodyNotEmpty = !isEmpty(get(pdfPageContentCopy, 'table.body', false));

      if (isElemTable && isTableBodyNotEmpty) {
        pdfPageContentCopy = this.fixTableWidths(pdfPageContentCopy);
        pdfPageContentCopy = this.fixPdfTableContent(pdfPageContentCopy, IMAGE_KEY_WORD, index);
      }

      return pdfPageContentCopy;
    },

    /**
     * Check if the Clipboard API is supported
     * This is supported in Chrome 66+, Firefox 63+, Safari 10.1+ and Edge 79+
     * Not supported over HTTP
     */
    isClipboardApiSupported() {
      return !!(
        navigator.clipboard
        && navigator.clipboard.write
      );
    },

    copylistener(e, html) {
      e.clipboardData.setData('text/html', html);
      e.clipboardData.setData('text/plain', html);
      e.preventDefault();
    },

    async copyHtmlToClipboard(html) {
      if (!this.isClipboardApiSupported()) {
        this.fallbackCopyHtmlToClipboard(html);
        return;
      }

      try {
        // Get the plain text version of the HTML content to use as fallback
        // We utilize the browsers own html rendering to convert the HTML to plain text
        const tempElement = document.createElement('div');
        tempElement.innerHTML = html;
        const plainText = tempElement.innerText;

        // Use the Clipboard API
        const blobHtml = new Blob([html], { type: 'text/html' });
        const blobText = new Blob([plainText], { type: 'text/plain' });
        const data = [new ClipboardItem({
          // For this to work on IOS and Safari, we need to provide a plain text version as well
          'text/html': blobHtml,
          'text/plain': blobText,
        })];

        await navigator.clipboard.write(data);
      } catch (err) {
        if (err.message === 'Write permission denied.') {
          this.fallbackCopyHtmlToClipboard(html);
          return;
        }

        throw err;
      }
    }, // copyHtmlToClipboard

    /**
     * Fallback for browsers that do not support the Clipboard API
     * This has limitations on copying html content and known issues on IOS devices
     * This method is also deprecated and will be removed in the future
     */
    fallbackCopyHtmlToClipboard(html) {
      document.addEventListener('copy', (e) => { this.copylistener(e, html); });
      document.execCommand('copy');
      document.removeEventListener('copy', (e) => { this.copylistener(e, html); });
    },

  },
};
